/**
 * Mapbox Configuration
 *
 * Centralized location for Mapbox access tokens and configuration.
 *
 * Required environment variables:
 * - MAPBOX_ACCESS_TOKEN (Node.js server-side)
 * - NEXT_PUBLIC_MAPBOX_ACCESS_TOKEN (Next.js client-side)
 * - VITE_MAPBOX_ACCESS_TOKEN (Vite/Storybook)
 *
 * Set one of these in your .env file to enable maps.
 */

/**
 * Get the Mapbox access token from environment variables
 * Checks multiple environment variable names in order of preference:
 * 1. MAPBOX_ACCESS_TOKEN (Node.js server-side)
 * 2. NEXT_PUBLIC_MAPBOX_ACCESS_TOKEN (Next.js client-side - bundled to browser)
 * 3. VITE_MAPBOX_ACCESS_TOKEN (Vite/Storybook - bundled to browser)
 * Returns undefined if not found (caller should handle gracefully)
 */
export function getMapboxTokenFromEnv(): string | undefined {
  // Check Node.js/Next.js server-side environment
  if (typeof process !== "undefined") {
    // Server-side token (Node.js/Next.js server components)
    if (process.env?.MAPBOX_ACCESS_TOKEN) {
      return process.env.MAPBOX_ACCESS_TOKEN;
    }
    // Next.js client-side token (available in browser after bundling)
    if (process.env?.NEXT_PUBLIC_MAPBOX_ACCESS_TOKEN) {
      return process.env.NEXT_PUBLIC_MAPBOX_ACCESS_TOKEN;
    }
  }

  // Check Vite/Storybook browser environment
  if (typeof import.meta !== "undefined") {
    const meta = import.meta as { env?: Record<string, string> };
    if (meta.env?.VITE_MAPBOX_ACCESS_TOKEN) {
      return meta.env.VITE_MAPBOX_ACCESS_TOKEN;
    }
  }

  // Return undefined instead of throwing - let the consuming component decide how to handle
  return undefined;
}
